import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Share2, Check } from "lucide-react";
import { QUESTIONS, RESULTS, AFTER_RESULT } from "./quizData";
import { ENROLL_URL } from "../../config";

const TEAL = "#4FB3A9";
const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -18 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const computeResult = (answers) => {
  const counts = {};
  answers.forEach((a) => (counts[a] = (counts[a] || 0) + 1));
  const max = Math.max(...Object.values(counts));
  const tied = Object.keys(counts).filter((k) => counts[k] === max);
  if (tied.length === 1) return tied[0];
  if (tied.includes(answers[4])) return answers[4];
  if (tied.includes(answers[0])) return answers[0];
  return tied[0];
};

const Para = ({ children, className = "" }) => (
  <p className={`whitespace-pre-line font-sans text-base font-light leading-relaxed text-white/75 md:text-lg ${className}`}>
    {children}
  </p>
);

const QuizButton = ({ children, onClick, className = "", ...rest }) => (
  <button
    onClick={onClick}
    className={`group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-ink ${className}`}
    {...rest}
  >
    <span>{children}</span>
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
  </button>
);

export const Quiz = () => {
  const [stage, setStage] = useState("intro");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [revealPhase, setRevealPhase] = useState(0);
  const [shared, setShared] = useState(false);

  const shareResult = async (name) => {
    const url = `${window.location.origin}/creatingwealth`;
    const text = `I took the \u201cWhat's Really Keeping You Stuck?\u201d quiz and my pattern is ${name}. Find yours:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "What's Really Keeping You Stuck?", text, url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = `${text} ${url}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setShared(true);
    setTimeout(() => setShared(false), 2500);
  };

  useEffect(() => {
    if (stage !== "calculating") return;
    const t1 = setTimeout(() => setRevealPhase(1), 1500);
    const t2 = setTimeout(() => setStage("result"), 2900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [stage]);

  const select = (key) => {
    const next = [...answers];
    next[step] = key;
    setAnswers(next);
    if (step < 4) setTimeout(() => setStep((s) => s + 1), 420);
  };

  const restart = () => {
    setStage("intro");
    setStep(0);
    setAnswers([]);
    setRevealPhase(0);
  };

  const result = stage === "result" ? RESULTS[computeResult(answers)] : null;

  return (
    <div data-testid="wealth-quiz" className="mt-24 bg-ink px-6 py-16 md:px-16 md:py-20">
      <AnimatePresence mode="wait">
        {stage === "intro" && (
          <motion.div key="intro" {...fade} className="mx-auto max-w-2xl text-center">
            <span className="font-sans text-[0.7rem] font-medium uppercase tracking-[0.32em]" style={{ color: TEAL }}>
              A two-minute coaching moment
            </span>
            <h3 className="mt-6 font-display text-3xl font-medium leading-[1.08] tracking-tight text-white sm:text-4xl md:text-5xl">
              What's really keeping <span className="italic" style={{ color: TEAL }}>you</span> stuck?
            </h3>
            <p className="mt-6 font-sans text-lg font-light leading-relaxed text-white/70">
              5 questions to uncover the pattern that may be standing between you and your next level of wealth.
            </p>
            <p className="mt-5 font-sans text-base font-light leading-relaxed text-white/60">
              You don't need another lecture about working harder. You might need to see something about yourself that
              you haven't been able to see from the inside.
            </p>
            <p className="mt-5 font-sans text-base font-light leading-relaxed text-white/60">
              Answer these five questions honestly. Don't overthink them.
              <br />
              There are no right answers. Just patterns.
            </p>
            <div className="mt-10">
              <QuizButton data-testid="quiz-start-btn" onClick={() => setStage("question")}>
                Let's find out
              </QuizButton>
            </div>
            <p className="mt-4 font-sans text-xs font-light tracking-wide text-white/40">Takes about 2 minutes.</p>
          </motion.div>
        )}

        {stage === "question" && (
          <motion.div key={`q-${step}`} {...fade} className="mx-auto max-w-2xl">
            <div className="flex items-center justify-between">
              <span data-testid="quiz-progress-label" className="font-sans text-[0.65rem] font-medium uppercase tracking-[0.3em] text-white/50">
                Question {step + 1} of 5
              </span>
              {step > 0 && (
                <button
                  data-testid="quiz-back-btn"
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-2 font-sans text-[0.65rem] font-medium uppercase tracking-[0.24em] text-white/50 transition-colors duration-300 hover:text-white"
                >
                  <ArrowLeft className="h-3 w-3" strokeWidth={1.6} /> Back
                </button>
              )}
            </div>
            <div className="mt-3 h-px w-full bg-white/15">
              <motion.div
                className="h-px"
                style={{ backgroundColor: TEAL }}
                initial={false}
                animate={{ width: `${((step + 1) / 5) * 100}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <h3 className="mt-10 font-display text-2xl font-medium leading-snug text-white md:text-3xl">
              {QUESTIONS[step].q}
            </h3>
            <div className="mt-8 space-y-3">
              {QUESTIONS[step].answers.map((a) => {
                const selected = answers[step] === a.key;
                return (
                  <button
                    key={a.key}
                    data-testid={`quiz-q${step + 1}-option-${a.key.toLowerCase()}`}
                    onClick={() => select(a.key)}
                    className={`block w-full border px-6 py-4 text-left font-sans text-base font-light leading-relaxed transition-[border-color,background-color,color] duration-300 ${
                      selected
                        ? "border-terracotta bg-terracotta/15 text-white"
                        : "border-white/15 text-white/75 hover:border-white/45 hover:text-white"
                    }`}
                  >
                    {a.text}
                  </button>
                );
              })}
            </div>
            {step === 4 && answers[4] && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10">
                <QuizButton data-testid="quiz-reveal-btn" onClick={() => setStage("calculating")}>
                  Show me what's been keeping me stuck
                </QuizButton>
              </motion.div>
            )}
          </motion.div>
        )}

        {stage === "calculating" && (
          <motion.div key="calc" {...fade} className="mx-auto flex min-h-[280px] max-w-2xl items-center justify-center text-center">
            <AnimatePresence mode="wait">
              <motion.p
                key={revealPhase}
                data-testid="quiz-transition-text"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6 }}
                className="font-display text-2xl font-medium italic text-white/85 md:text-3xl"
              >
                {revealPhase === 0 ? "Looking for your pattern..." : "Ohhh. There it is."}
              </motion.p>
            </AnimatePresence>
          </motion.div>
        )}

        {stage === "result" && result && (
          <motion.div key="result" {...fade} className="mx-auto max-w-2xl" data-testid="quiz-result">
            <span className="font-sans text-[0.7rem] font-medium uppercase tracking-[0.32em]" style={{ color: TEAL }}>
              Your pattern
            </span>
            <h3 data-testid="quiz-result-name" className="mt-5 font-display text-3xl font-medium leading-[1.08] tracking-tight text-white sm:text-4xl md:text-5xl">
              You're <span className="italic" style={{ color: TEAL }}>{result.name}</span>.
            </h3>
            <p className="mt-4 font-display text-xl font-medium leading-snug text-white/90 md:text-2xl">{result.headline}</p>
            <button
              data-testid="quiz-share-btn"
              onClick={() => shareResult(result.name)}
              className="group mt-6 inline-flex items-center gap-2.5 border border-white/25 px-5 py-2.5 font-sans text-[0.65rem] font-medium uppercase tracking-[0.24em] text-white/70 transition-[border-color,color] duration-300 hover:border-terracotta hover:text-white"
            >
              {shared ? (
                <>
                  <Check className="h-3.5 w-3.5" style={{ color: TEAL }} strokeWidth={1.8} />
                  <span>Copied to clipboard</span>
                </>
              ) : (
                <>
                  <Share2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                  <span>Share my result</span>
                </>
              )}
            </button>
            <div className="mt-8 space-y-5">
              {result.body.map((p, i) => (
                <Para key={i}>{p}</Para>
              ))}
            </div>

            <div className="mt-12 border-l-2 pl-6" style={{ borderColor: TEAL }}>
              <span className="font-sans text-[0.65rem] font-medium uppercase tracking-[0.3em]" style={{ color: TEAL }}>
                Your question
              </span>
              <p className="mt-3 font-display text-xl font-medium italic leading-snug text-white md:text-2xl">
                {result.question}
              </p>
            </div>

            <div className="mt-10 bg-white/5 p-8">
              <span className="font-sans text-[0.65rem] font-medium uppercase tracking-[0.3em]" style={{ color: TEAL }}>
                Try this
              </span>
              <div className="mt-4 space-y-4">
                {result.exercise.map((p, i) => (
                  <Para key={i}>{p}</Para>
                ))}
              </div>
            </div>

            <div className="mt-16 border-t border-white/10 pt-12">
              <p className="font-display text-2xl font-medium leading-snug text-white md:text-3xl">{AFTER_RESULT.lead}</p>
              <div className="mt-6 space-y-5">
                {AFTER_RESULT.paragraphs.map((p, i) => (
                  <Para key={i}>{p}</Para>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {AFTER_RESULT.path.map((word, i) => (
                  <span key={word} className="flex items-center gap-3">
                    <span className="font-display text-lg font-medium tracking-wide text-white md:text-xl">{word}</span>
                    {i < AFTER_RESULT.path.length - 1 && (
                      <ArrowRight className="h-4 w-4" style={{ color: TEAL }} strokeWidth={1.6} />
                    )}
                  </span>
                ))}
              </div>
              <div className="mt-8 space-y-3">
                {AFTER_RESULT.bullets.map((b) => (
                  <div key={b} className="flex items-start gap-4">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
                    <span className="font-sans text-base font-light leading-relaxed text-white/75">{b}</span>
                  </div>
                ))}
              </div>
              <Para className="mt-8">{AFTER_RESULT.closing}</Para>

              <div className="mt-10 flex flex-col items-start gap-5">
                <a
                  href={ENROLL_URL}
                  data-testid="quiz-primary-cta"
                  {...(/^https?:\/\//.test(ENROLL_URL) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-ink"
                >
                  <span>Join as a Founding Member — $67/month</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
                </a>
                <p className="font-sans text-xs font-light leading-relaxed text-white/45">
                  Cancel anytime. Your founding member rate stays $67/month for as long as you remain an active member.
                </p>
                <a
                  href="#journey"
                  data-testid="quiz-secondary-cta"
                  className="group inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                >
                  <span>Show me the full journey</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
                </a>
                <button
                  data-testid="quiz-retake-btn"
                  onClick={restart}
                  className="mt-2 font-sans text-[0.65rem] font-medium uppercase tracking-[0.24em] text-white/40 transition-colors duration-300 hover:text-white"
                >
                  Retake the quiz
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
